/**
 * SeverityFilter
 * Filter toggle buttons for alert severities
 */

import { AlertTriangle, AlertCircle, Info } from 'lucide-react';
import { Button } from '@/core/components/ui/button';
import { AlertSeverity } from '../../types/dashboard-types';
import { cn } from '@/core/lib/utils';

interface SeverityFilterProps {
  selectedSeverities: AlertSeverity[];
  onToggle: (severity: AlertSeverity) => void;
  onClear: () => void;
  counts?: {
    critical: number;
    warning: number;
    info: number;
  };
}

const SEVERITY_CONFIG: {
  value: AlertSeverity;
  label: string;
  icon: React.ReactNode;
  activeClass: string;
  countBg: string;
}[] = [
  {
    value: 'Critical',
    label: 'Critical',
    icon: <AlertCircle className="h-4 w-4" />,
    activeClass: 'bg-red-600 text-white hover:bg-red-700',
    countBg: 'bg-red-500',
  },
  {
    value: 'Warning',
    label: 'Warning',
    icon: <AlertTriangle className="h-4 w-4" />,
    activeClass: 'bg-yellow-500 text-white hover:bg-yellow-600',
    countBg: 'bg-yellow-500',
  },
  {
    value: 'Info',
    label: 'Info',
    icon: <Info className="h-4 w-4" />,
    activeClass: 'bg-blue-600 text-white hover:bg-blue-700',
    countBg: 'bg-blue-500',
  },
];

export function SeverityFilter({
  selectedSeverities,
  onToggle,
  onClear,
  counts,
}: SeverityFilterProps) {
  const hasSelection = selectedSeverities.length > 0;

  const getCount = (severity: AlertSeverity): number => {
    if (!counts) return 0;
    switch (severity) {
      case 'Critical':
        return counts.critical;
      case 'Warning':
        return counts.warning;
      case 'Info':
        return counts.info;
      default:
        return 0;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <span className="text-sm font-medium text-gray-700">Filter by:</span>
      <div className="flex items-center gap-1">
        {SEVERITY_CONFIG.map((config) => {
          const isSelected = selectedSeverities.includes(config.value);
          const count = getCount(config.value);

          return (
            <Button
              key={config.value}
              variant="outline"
              size="sm"
              onClick={() => onToggle(config.value)}
              className={cn(
                'h-8 gap-1.5',
                isSelected && config.activeClass,
                !isSelected && 'hover:bg-gray-100'
              )}
            >
              {config.icon}
              {config.label}
              {counts && count > 0 && (
                <span
                  className={cn(
                    'text-xs px-1.5 py-0.5 rounded-full',
                    isSelected ? 'bg-white/20 text-white' : config.countBg + ' text-white'
                  )}
                >
                  {count}
                </span>
              )}
            </Button>
          );
        })}
      </div>
      {hasSelection && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onClear}
          className="h-8 text-gray-500"
        >
          Clear
        </Button>
      )}
    </div>
  );
}
